import {
  AlloggiamentoFiltroDto,
  AnalisiDto,
  BonificaDto,
  getGetGrafoNodoQueryKey,
  getGetNodoIconaImageQueryKey,
  getGetNodoPortaleQueryKey,
  getGetNodoQueryKey,
  NodoDto,
  useEliminaNodo,
} from "@/api";
import AnalisiIcon from "@/assets/analisi.png";
import BonificaIcon from "@/assets/bonifica.png";
import CentraleIcon from "@/assets/centrale-icon2.png";
import ErrorIcon from "@/assets/error.png";
import FiltroIcon from "@/assets/filtro.png";
import OkIcon from "@/assets/ok.png";
import UnknownIcon from "@/assets/unknown.png";
import WarningIcon from "@/assets/warning.png";
import PosizioneNodo from "@/components/PosizioneNodo";
import { useAuth } from "@/context/useAuth";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import Typography from "@/elements/Typography";
import { portaleConfig } from "@/portaliConfig";
import {
  ScadenzaAnalisiWarningDays,
  ScadenzaFiltroWarningDays,
} from "@/utils/scadenzeUtils";
import { Icon, Tooltip } from "@mui/material";
import Card from "@mui/material/Card";
import { useQueryClient } from "@tanstack/react-query";
import { some } from "lodash";
import moment from "moment";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import GatewayIcon from "@/assets/gateway.png";
import RipetitoreIcon from "@/assets/repeater.png";

function NodoCard({ nodo }: { nodo: NodoDto }) {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate } = useEliminaNodo();

  function handleDelete() {
    const message = t("nodoCard.confermaElimina", { nome: nodo.nome });
    if (!window.confirm(message)) {
      return;
    }

    mutate(
      { id: nodo.id },
      {
        onSuccess: () => {
          toast.success(t("nodoCard.toast.eliminata"));
          if (nodo.breadcrumbs) {
            const azienda = nodo.breadcrumbs[0];
            const padre = nodo.breadcrumbs[nodo.breadcrumbs.length - 1];

            if (azienda) {
              queryClient
                .invalidateQueries(getGetGrafoNodoQueryKey(azienda.nodo.id))
                .then();
            }
            if (padre) {
              queryClient
                .invalidateQueries(getGetNodoQueryKey(padre.nodo.id))
                .then();
              queryClient
                .invalidateQueries(
                  getGetNodoPortaleQueryKey(portaleConfig.id, padre.nodo.id)
                )
                .then();
            }
          }
        },
        onError: () => {
          toast.error(t("nodoCard.toast.erroreEliminazione"));
        },
      }
    );
  }

  const link = `/nodi/${nodo.id}`;

  const { iconaTitolo, iconaSrc } = useMemo(() => {
    if (nodo.tipo === "Gateway") {
      return {
        iconaTitolo: t("grafo.gateway"),
        iconaSrc: GatewayIcon,
      };
    } else if (nodo.tipo === "Ripetitore") {
      return {
        iconaTitolo: t("grafo.ripetitore"),
        iconaSrc: RipetitoreIcon,
      };
    } else {
      const icona = nodo.contenitoreIcona;
      const iconaTitolo = icona?.nome ?? t("grafo.centrale");
      const iconaSrc = icona?.id
        ? getGetNodoIconaImageQueryKey(icona.id)[0]
        : CentraleIcon;

      return { iconaTitolo, iconaSrc };
    }
  }, [nodo, t]);

  return (
    <Card
      sx={{
        maxWidth: 700,
        minWidth: {
          xs: "100%",
          md: 550,
        },
        flex: 1,
        flexDirection: "row",
        height: "100%",
      }}
    >
      <Box p={1}>
        <img src={iconaSrc} width={60} alt={iconaTitolo} />
      </Box>
      <Box p={2} flex={1}>
        <Box display="flex" flexDirection="column" height="100%">
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            flexWrap="wrap"
            columnGap={2}
          >
            <Box display="flex" flexDirection="column">
              <Typography variant="body2" color="text" fontWeight="medium">
                {iconaTitolo}
              </Typography>

              <Typography variant="h5" fontWeight="bold">
                {nodo.nome}
              </Typography>

              <Box sx={{ minHeight: 18, mb: 1 }}>
                <PosizioneNodo nodo={nodo} />
              </Box>
            </Box>
            <Box display="flex" gap={1} style={{ minWidth: 45 }}>
              {nodo.haAnalisi && (
                <StatoAnalisiIcon ultimaAnalisi={nodo.ultimaAnalisi} />
              )}
              {nodo.haStoricoBonifiche && (
                <StatoBonificaIcon ultimaBonifica={nodo.ultimaBonifica} />
              )}

              {nodo.haAlloggiamentiFiltro && (
                <StatoFiltriIcon
                  alloggiamentiFiltro={nodo.alloggiamentiFiltro}
                />
              )}
            </Box>
          </Box>

          <Box
            mt={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              variant="textArrow"
              color="text"
              LinkComponent={Link}
              to={link}
              sx={{ m: 0 }}
            >
              {t("azioni.apri")}
            </Button>

            {hasPermission("ContenitoreDelete") && (
              <Button
                startIcon={<Icon>delete_outlined</Icon>}
                variant="text"
                color="error"
                size="small"
                onClick={handleDelete}
              >
                {t("azioni.elimina")}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Card>
  );
}

export default NodoCard;

function StatoFiltriIcon({
  alloggiamentiFiltro,
}: {
  alloggiamentiFiltro?: AlloggiamentoFiltroDto[] | null;
}) {
  const { t } = useTranslation();
  const [status, text] = useMemo(() => {
    const scadenze =
      alloggiamentiFiltro?.map((f) => {
        if (!f.filtroDaSostituireDopoIl) {
          return null;
        }
        return moment(f.filtroDaSostituireDopoIl).diff(moment(), "days", true);
      }) ?? [];

    if (scadenze.length <= 0) {
      return [
        "unknown",
        t("nodoCard.filtri.nessunFiltroMappato"),
      ] as const;
    } else if (some(scadenze, (s) => s === null)) {
      return [
        "unknown",
        t("nodoCard.filtri.scadenzaSconosciuta"),
      ] as const;
    } else if (some(scadenze, (s) => s !== null && s <= 0)) {
      return ["urgent", t("nodoCard.filtri.daSostituire")] as const;
    } else if (
      some(scadenze, (s) => s !== null && s <= ScadenzaFiltroWarningDays)
    ) {
      return [
        "warning",
        t("nodoCard.filtri.daSostituireEntro", {
          giorni: ScadenzaFiltroWarningDays,
        }),
      ] as const;
    }

    return ["ok", t("nodoCard.filtri.ok")] as const;
  }, [alloggiamentiFiltro, t]);
  return <ScadenzaIcon icon={FiltroIcon} status={status} text={text} />;
}

function StatoAnalisiIcon({
  ultimaAnalisi,
}: {
  ultimaAnalisi?: AnalisiDto | null;
}) {
  const { t } = useTranslation();
  const [status, text] = useMemo(() => {
    if (!ultimaAnalisi) {
      return ["unknown", t("nodoCard.analisi.nessuna")] as const;
    }

    // TODO: mostrare errore se l'analisi non è valida?

    const scadenza = ultimaAnalisi.validaFinoA;
    if (!scadenza) {
      return ["unknown", t("nodoCard.analisi.validitaSconosciuta")] as const;
    }

    const days = moment(scadenza).diff(moment(), "days", true);
    if (days <= 0) {
      return [
        "urgent",
        t("nodoCard.analisi.scaduteIl", {
          data: moment(scadenza).format("L"),
        }),
      ] as const;
    } else if (days <= ScadenzaAnalisiWarningDays) {
      return [
        "warning",
        t("nodoCard.analisi.valideFinoAl", {
          data: moment(scadenza).format("L"),
        }),
      ] as const;
    }

    return [
      "ok",
      t("nodoCard.analisi.valideFinoAl", {
        data: moment(scadenza).format("L"),
      }),
    ] as const;
  }, [ultimaAnalisi, t]);
  return <ScadenzaIcon icon={AnalisiIcon} status={status} text={text} />;
}

function StatoBonificaIcon({
  ultimaBonifica,
}: {
  ultimaBonifica?: BonificaDto | null;
}) {
  const { t } = useTranslation();
  const [status, text] = useMemo(() => {
    if (!ultimaBonifica) {
      return ["unknown", t("nodoCard.bonifiche.nessuna")] as const;
    }

    const scadenza = ultimaBonifica.scadenza;
    if (!scadenza) {
      return [
        "unknown",
        t("nodoCard.bonifiche.scadenzaSconosciuta"),
      ] as const;
    }

    const days = moment(scadenza).diff(moment(), "days", true);
    if (days <= 0) {
      return [
        "urgent",
        t("nodoCard.bonifiche.scadutaIl", {
          data: moment(scadenza).format("L"),
        }),
      ] as const;
    } else if (days <= ScadenzaAnalisiWarningDays) {
      return [
        "warning",
        t("nodoCard.bonifiche.scadeIl", {
          data: moment(scadenza).format("L"),
        }),
      ] as const;
    }

    return [
      "ok",
      t("nodoCard.bonifiche.scadeIl", {
        data: moment(scadenza).format("L"),
      }),
    ] as const;
  }, [ultimaBonifica, t]);
  return <ScadenzaIcon icon={BonificaIcon} status={status} text={text} />;
}

function ScadenzaIcon({
  icon,
  status,
  text,
}: {
  icon: string;
  status: "unknown" | "ok" | "warning" | "urgent";
  text: string;
}) {
  const statusImage =
    status === "unknown"
      ? UnknownIcon
      : status === "ok"
      ? OkIcon
      : status === "warning"
      ? WarningIcon
      : status === "urgent"
      ? ErrorIcon
      : null;

  const iconSize = 28;

  return (
    <Tooltip title={text}>
      <div style={{ position: "relative", height: iconSize }}>
        <img
          src={icon}
          width={iconSize}
          style={{ opacity: status === "ok" ? 1 : 0.6 }}
          alt={text}
        />
        {statusImage && (
          <img
            style={{ position: "absolute", bottom: 0, right: 0 }}
            src={statusImage}
            width={15}
            alt={text}
          />
        )}
      </div>
    </Tooltip>
  );
}
